import React, { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';

/**
 * Camera-based QR scanner modal — a fallback for the Bluetooth scanner.
 *
 * Uses html5-qrcode (works on iOS Safari 14+, Chrome, Edge, Firefox). On scan:
 * fires `onDetected(code)` once, briefly debounces duplicate scans of the same
 * code, and stays open so the user can scan several codes in a row. Close
 * dismisses the camera.
 *
 * Props:
 *   open      — render-controls visibility (component returns null when false)
 *   onClose   — called when the user dismisses
 *   onDetected(code) — called with the decoded QR text; debounced so the
 *                     same code fires at most every ~1.5s
 */
export default function CameraScanner({ open, onClose, onDetected }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const lastScanRef = useRef({ code: '', at: 0 });
  const [error, setError] = useState(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    let scanner;

    const start = async () => {
      try {
        scanner = new Html5Qrcode('wfc-camera-reader', { verbose: false });
        scannerRef.current = scanner;

        // Prefer the rear camera on phones; fall back to any available camera.
        // html5-qrcode accepts facingMode as a plain string or { exact: ... };
        // it doesn't accept the browser-native { ideal: ... } shape.
        const cameraConfig = { facingMode: 'environment' };
        const config = {
          fps: 12,
          qrbox: (vw, vh) => {
            const min = Math.min(vw, vh);
            const size = Math.floor(min * 0.7);
            return { width: size, height: size };
          },
          aspectRatio: 1,
        };

        await scanner.start(
          cameraConfig,
          config,
          (decoded) => {
            if (cancelled) return;
            const code = (decoded || '').trim().toUpperCase();
            if (!code) return;
            const now = Date.now();
            if (
              lastScanRef.current.code === code &&
              now - lastScanRef.current.at < 1500
            ) {
              return; // duplicate within debounce window — ignore
            }
            lastScanRef.current = { code, at: now };
            onDetected(code);
          },
          () => {
            /* per-frame "no QR found" — silent */
          },
        );
        if (!cancelled) setActive(true);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.message || String(e);
        // Common failure modes — give the user something actionable
        if (/NotAllowed|Permission/i.test(msg)) {
          setError(
            'Camera permission denied. Allow camera access in your browser, then try again.',
          );
        } else if (/NotFound|no camera/i.test(msg)) {
          setError('No camera found on this device.');
        } else if (/NotReadable|in use/i.test(msg)) {
          setError('Camera is in use by another app. Close it and try again.');
        } else {
          setError(`Could not start camera: ${msg}`);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s && s.isScanning) {
        s.stop()
          .then(() => s.clear())
          .catch(() => {
            /* best-effort */
          });
      }
      scannerRef.current = null;
      setActive(false);
      setError(null);
    };
  }, [open, onDetected]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
      onClick={onClose}
      data-testid="camera-scanner-modal"
    >
      <div
        className="surface rounded-lg w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-900">
          <div className="flex items-center gap-2">
            <Camera size={14} className="text-amber-400" />
            <h2 className="text-sm font-semibold text-white">Camera scanner</h2>
            {active && (
              <span className="text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white"
            data-testid="camera-scanner-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error ? (
            <div className="rounded border border-rose-500/30 bg-rose-500/5 text-rose-200 text-xs p-3 leading-relaxed">
              {error}
            </div>
          ) : (
            <p className="text-[11px] text-neutral-400 leading-relaxed">
              Point the camera at a QR label. Scans feed the same flow as the Bluetooth
              scanner — keep this open to scan several items in a row.
            </p>
          )}

          <div
            id="wfc-camera-reader"
            ref={containerRef}
            className="rounded-md overflow-hidden border border-neutral-800 bg-neutral-950 aspect-square w-full"
            data-testid="camera-scanner-viewport"
          />

          <p className="text-[10px] text-neutral-500 leading-relaxed">
            Tip: in dim light, tilt the QR slightly to avoid camera glare.
            iPhone users — make sure you&apos;ve allowed Safari camera access
            in Settings → Safari.
          </p>
        </div>

        <div className="px-4 py-3 border-t border-neutral-900 flex items-center justify-end">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-md border border-neutral-800 text-neutral-300 hover:bg-neutral-900"
            data-testid="camera-scanner-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
